import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../config/api';

interface Aircraft {
  _id: string;
  identification: string;
  type: string;
}

interface FlightTrack {
  _id: string;
  userid: string;
  aircraftid: Aircraft | null;
  dateStart: string;
  dateEnd: string;
  duration: number;
  region?: string;
}

const ActivitiesTable: React.FC = () => {
  const [flights, setFlights] = useState<FlightTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFlights = async () => {
      try {
        const response = await apiRequest('/flight-tracks');
        if (!response.ok) {
          throw new Error('Fehler beim Laden der Flüge');
        }
        const data = await response.json();
        // Filtere nur Flüge mit gültigem Aircraft und sortiere nach Startzeit absteigend
        const validFlights = data.filter((f: FlightTrack) => f.aircraftid);
        const sortedFlights = validFlights.sort((a: FlightTrack, b: FlightTrack) =>
          new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime()
        ).slice(0, 6);
        setFlights(sortedFlights);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
      } finally {
        setLoading(false);
      }
    };

    fetchFlights();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDuration = (durationSeconds: number) => {
    const hours = Math.floor(durationSeconds / 3600);
    const minutes = Math.floor((durationSeconds % 3600) / 60);
    return `${hours}:${minutes.toString().padStart(2, '0')} h`;
  };

  return (
    <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
      <div className="card-body p-4">
        <h4 className="mb-4" style={{ fontWeight: 700, color: '#687088' }}>Letzte Aktivitäten</h4>
        <div className="table-responsive">
          {loading ? (
            <p style={{ color: '#687088' }}>Lade Flüge...</p>
          ) : error ? (
            <p style={{ color: '#dc3545' }}>{error}</p>
          ) : flights.length === 0 ? (
            <p style={{ color: '#687088' }}>Keine Flüge vorhanden.</p>
          ) : (
            <table className="table" style={{ color: '#687088' }}>
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Gerät</th>
                  <th>Kennung</th>
                  <th>Dauer</th>
                  <th>Region</th>
                </tr>
              </thead>
              <tbody>
                {flights.map((flight) => {
                  const AIRCRAFT_LABELS: Record<string, string> = {
                    balloon: 'Ballon', ballon: 'Ballon',
                    glider: 'Segelflugzeug', segelflugzeug: 'Segelflugzeug',
                    plane: 'Motorflugzeug', motorflugzeug: 'Motorflugzeug',
                    helicopter: 'Hubschrauber', hubschrauber: 'Hubschrauber',
                  };
                  const rawType = flight.aircraftid?.type?.toLowerCase() || '';
                  const aircraftType = AIRCRAFT_LABELS[rawType] || flight.aircraftid?.type || '-';

                  return (
                    <tr key={flight._id}>
                      <td>{formatDate(flight.dateStart)}</td>
                      <td>{aircraftType}</td>
                      <td>{flight.aircraftid?.identification || '-'}</td>
                      <td>{formatDuration(flight.duration)}</td>
                      <td>{flight.region || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivitiesTable;
